import React, { useMemo } from 'react';
import { Button, Card, Form, OverlayTrigger, Stack, Tooltip } from 'react-bootstrap';
import useResponderDialog from 'features/Responder/hooks/useResponderDialog';
import { useZones } from 'features/Responder/features/Zones/contexts/ZonesContext';
import { deleteZoneWithConfirmation } from 'features/Responder/features/Zones/utils/deleteZoneWithConfirmation';

const infoIconStyle = {
  width: 16,
  height: 16,
  borderRadius: 999,
  fontSize: 10,
  fontWeight: 700,
  border: '1px solid rgba(255,255,255,0.25)',
  color: '#e8edf7',
  background: 'rgba(255,255,255,0.12)',
  cursor: 'help',
};

const cancelButtonStyle = {
  minWidth: 120,
  backgroundColor: '#dc3545',
  borderColor: '#dc3545',
  color: '#ffffff',
  transition: 'none',
  boxShadow: 'none',
};

const actionButtonStyle = {
  minWidth: 120,
};

const InfoIcon = ({ id, text }) => (
  <OverlayTrigger
    placement="top"
    overlay={
      <Tooltip id={id} className="responder-tooltip">
        {text}
      </Tooltip>
    }
  >
    <span className="d-inline-flex align-items-center justify-content-center" style={infoIconStyle}>
      i
    </span>
  </OverlayTrigger>
);

// Zone manager for zone notification settings.
export default function ZoneBuilder({ onPersistSuccess = () => {} }) {
  const { zones, updateZone, activeZoneId, saveZones, fetchZones, setZonesEditing, deleteZone } =
    useZones();
  const { confirm, showAlert, dialogElement } = useResponderDialog();
  const hasUnsavedChanges = useMemo(
    () => Array.isArray(zones) && zones.some((z) => z?.isModified),
    [zones],
  );

  const activeZone = useMemo(
    () => zones.find((zone) => `${zone.id}` === `${activeZoneId}`) || null,
    [activeZoneId, zones],
  );

  const parseNumber = (raw) => {
    if (raw === '' || raw == null) return null;
    const next = Number(raw);
    return Number.isFinite(next) ? next : null;
  };

  const handleParameterChange = (zone, key, raw) => {
    if (!zone?.id) return;
    const nextValue = parseNumber(raw);
    const nextParameters = { ...(zone.parameters || {}), [key]: nextValue };
    updateZone(zone.id, { parameters: nextParameters });
  };

  const hasZones = useMemo(() => Array.isArray(zones) && zones.length > 0, [zones]);

  const handleCancel = async () => {
    if (!hasUnsavedChanges) {
      setZonesEditing(false);
      return;
    }

    const shouldDiscard = await confirm({
      title: 'Discard changes',
      message: 'You have unsaved changes. Canceling will discard these changes. Continue?',
      confirmLabel: 'Discard',
      cancelLabel: 'Keep editing',
      confirmVariant: 'danger',
      cancelVariant: 'primary',
      showCancel: true,
    });
    if (!shouldDiscard) return;

    try {
      await fetchZones();
      setZonesEditing(false);
    } catch (error) {
      console.error('Failed to discard unsaved zone changes:', error);
      await showAlert({
        title: 'Discard failed',
        message: 'Unable to discard unsaved changes right now. Please try again.',
        confirmLabel: 'OK',
        confirmVariant: 'primary',
        showCancel: false,
      });
    }
  };

  const handleDelete = async () => {
    await deleteZoneWithConfirmation({ activeZone, zones, deleteZone, confirm, showAlert });
  };

  return (
    <>
      <Card className="shadow-sm h-100 w-100">
        <Card.Body className="d-flex flex-column gap-3 h-100" style={{ minHeight: 0 }}>
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <Card.Title className="mb-0">Zone Manager</Card.Title>
              <Card.Text className="text-muted small mb-0">
                Update notification settings for each zone below.
              </Card.Text>
            </div>
          </div>

          <div className="d-flex flex-column flex-grow-1" style={{ minHeight: 0 }}>
            <div className="fw-semibold mb-2">Notification Settings</div>
            <div className="flex-grow-1 overflow-auto" style={{ minHeight: 0 }}>
              {activeZone ? (
                <Stack gap={2}>
                  <Card key={activeZone.id || activeZone.name} className="shadow-sm">
                    <Card.Body className="p-2 d-flex flex-column gap-2">
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="fw-semibold small">
                          {activeZone.name || 'Untitled zone'}
                        </div>
                        <span
                          aria-hidden="true"
                          style={{
                            width: 16,
                            height: 16,
                            borderRadius: 999,
                            background: activeZone.fillColor || activeZone.strokeColor || '#f97316',
                            border: '1px solid rgba(255,255,255,0.35)',
                          }}
                        />
                      </div>
                      <div className="pt-1 border-top">
                        <div className="fw-semibold small mb-2">Notification Rules</div>
                        <Form.Group className="mb-2">
                          <div className="d-flex align-items-center gap-2 mb-1">
                            <Form.Label className="small text-muted mb-0">
                              Minimum occupants
                            </Form.Label>
                            <InfoIcon
                              id={`zone-${activeZone.id}-min-occupants`}
                              text="Minimum number of occupants required before the zone is considered active."
                            />
                          </div>
                          <Form.Control
                            type="number"
                            size="sm"
                            min={0}
                            placeholder="e.g., 1"
                            value={activeZone.parameters?.occupancyMin ?? ''}
                            onChange={(e) =>
                              handleParameterChange(activeZone, 'occupancyMin', e.target.value)
                            }
                          />
                        </Form.Group>
                        <Form.Group className="mb-2">
                          <div className="d-flex align-items-center gap-2 mb-1">
                            <Form.Label className="small text-muted mb-0">
                              Maximum occupants
                            </Form.Label>
                            <InfoIcon
                              id={`zone-${activeZone.id}-max-occupants`}
                              text="Maximum occupants allowed before the zone is flagged."
                            />
                          </div>
                          <Form.Control
                            type="number"
                            size="sm"
                            min={0}
                            placeholder="e.g., 10"
                            value={activeZone.parameters?.occupancyMax ?? ''}
                            onChange={(e) =>
                              handleParameterChange(activeZone, 'occupancyMax', e.target.value)
                            }
                          />
                        </Form.Group>
                        <Form.Group className="mb-2">
                          <div className="d-flex align-items-center gap-2 mb-1">
                            <Form.Label className="small text-muted mb-0">
                              Time of stay (minutes)
                            </Form.Label>
                            <InfoIcon
                              id={`zone-${activeZone.id}-time-stay`}
                              text="How long someone must remain in the zone before it is considered a valid stay."
                            />
                          </div>
                          <Form.Control
                            type="number"
                            size="sm"
                            min={0}
                            placeholder="e.g., 15"
                            value={activeZone.parameters?.timeOfStayMinutes ?? ''}
                            onChange={(e) =>
                              handleParameterChange(activeZone, 'timeOfStayMinutes', e.target.value)
                            }
                          />
                        </Form.Group>
                        <Form.Group>
                          <div className="d-flex align-items-center gap-2 mb-1">
                            <Form.Label className="small text-muted mb-0">
                              Debounce time (seconds)
                            </Form.Label>
                            <InfoIcon
                              id={`zone-${activeZone.id}-debounce`}
                              text="Grace period someone can be outside or inside the zone before the manager is notified."
                            />
                          </div>
                          <Form.Control
                            type="number"
                            size="sm"
                            min={0}
                            placeholder="e.g., 30"
                            value={activeZone.parameters?.debounceSeconds ?? ''}
                            onChange={(e) =>
                              handleParameterChange(activeZone, 'debounceSeconds', e.target.value)
                            }
                          />
                        </Form.Group>
                      </div>
                    </Card.Body>
                  </Card>
                </Stack>
              ) : (
                <div className="text-muted small">
                  {hasZones
                    ? 'Select a zone on the map to edit notification settings.'
                    : 'No zones yet. Add one from the Zone Tools panel.'}
                </div>
              )}
            </div>
          </div>

          <div className="d-flex gap-2">
            <Button size="sm" variant="danger" style={cancelButtonStyle} onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              size="sm"
              variant="danger"
              style={cancelButtonStyle}
              onClick={handleDelete}
              disabled={!activeZone}
            >
              Delete zone
            </Button>
            <Button
              size="sm"
              variant={hasUnsavedChanges ? 'primary' : 'secondary'}
              style={actionButtonStyle}
              disabled={!hasUnsavedChanges}
              onClick={async () => {
                try {
                  const persistResult = await saveZones();
                  setZonesEditing(false);
                  onPersistSuccess?.(persistResult);
                } catch (error) {
                  console.error('Failed to save zones:', error);
                }
              }}
            >
              Save changes
            </Button>
          </div>
        </Card.Body>
      </Card>
      {dialogElement}
    </>
  );
}
